import { QueryResult } from '@apollo/client';
import { Formik } from 'formik';
import {
  useUpdatePresentation,
  useUpdateView,
} from 'generated/api-client/@tanstack/react-query.gen';
import {
  CityProViewSetInput,
  Exact,
  GoogleReviewLinkInsertInput,
  GoogleReviewLinkSetInput,
  ProCertificationSetInput,
  ProFormUpdateFetchCompanyQuery,
  ProFormUpdateQuery,
  ProMediaObjectInsertInput,
  ProMediaObjectSetInput,
  ProMessageInsertInput,
  ProMessageSetInput,
  ProPostalAddressSetInput,
  ProPresentationSetInput,
  ProPriceInsertInput,
  ProPriceSetInput,
  ProViewProPersonSetInput,
  ProViewProViewSetInput,
  ProViewSetInput,
  TradeProPresentationInsertInput,
  TradeProViewSetInput,
} from 'generated/graphql';
import React, { FunctionComponent, useCallback, useEffect } from 'react';
import { Loader } from 'semantic-ui-react';
import { PhoneNumber, ToastError, ToastSuccess } from 'utils';
import { formatAddress } from 'utils/format-address';
import { addCrs, isPoint } from 'utils/geometry';
import { isDeepEqual } from 'utils/is-deep-equal';
import removeProperties from 'utils/remove-properties';
import {
  ProFormUpdateFields,
  ProMediaObjectFields,
  ProMessageFields,
  ProPriceFields,
} from './pro-form-update-fields-type';
import { ProFormUpdateProParams } from './pro-form-update-repository';
import { proFormUpdateValidationSchema } from './pro-form-update-validation-schema';
import ProFormUpdateView from './pro-form-update-view';

type ProFormUpdateContainerProps = {
  proView?: ProFormUpdateQuery['proViewByPk'];
  proPresentationId: number;
  updateGoogleReviewLink: (
    id: number,
    googleReviewLink: GoogleReviewLinkSetInput,
  ) => Promise<void>;
  updateProViewGoogleReviewLink: (
    proViewId: number,
    googleReviewLinkId: number,
  ) => Promise<void>;
  updateProMediaObject: (
    id: number,
    proMediaObject: ProMediaObjectSetInput,
  ) => Promise<void>;
  updateProMessage: (
    id: number,
    proMessage: ProMessageSetInput,
  ) => Promise<void>;
  updateProPrice: (id: number, proPrice: ProPriceSetInput) => Promise<void>;
  updateProIntervention: (proViewId: number) => Promise<void>;
  updateProViewProPerson: (
    proPersonId: number,
    proViewId: number,
    proViewProPerson: ProViewProPersonSetInput,
  ) => Promise<void>;
  updatePro: (variables: ProFormUpdateProParams) => Promise<void>;
  updatePresentation: (variables: {
    id: number;
    presentation: ProPresentationSetInput;
  }) => Promise<void>;
  createSlugRedirect: (fromSlug: string, toSlug: string) => Promise<void>;
  createGoogleReviewLink: (
    googleReviewLink: GoogleReviewLinkInsertInput,
  ) => Promise<number | undefined>;
  moveContract: (
    oldCompanyId: number,
    newCompanyId: number,
    proViewId: number,
  ) => Promise<void>;
  fetchProForm: () => Promise<
    QueryResult<
      ProFormUpdateQuery,
      Exact<{
        id: number;
      }>
    >
  >;
  fetchCompany: (companyId: number) => Promise<
    QueryResult<
      ProFormUpdateFetchCompanyQuery,
      Exact<{
        id: number;
      }>
    >
  >;
};

const ProFormUpdateContainer: FunctionComponent<
  ProFormUpdateContainerProps
> = ({
  proView,
  proPresentationId,
  updateGoogleReviewLink,
  updateProViewGoogleReviewLink,
  updateProMediaObject,
  updateProMessage,
  updateProPrice,
  updatePro,
  updatePresentation,
  updateProViewProPerson,
  updateProIntervention,
  createSlugRedirect,
  createGoogleReviewLink,
  fetchProForm,
  moveContract,
  fetchCompany,
}) => {
  const { mutateAsync: updateProViewAsync } = useUpdateView();
  const { mutateAsync: updatePresentationAsync } = useUpdatePresentation();

  const onSubmit = useCallback(
    async (values: ProFormUpdateFields) => {
      // Should submit only when proView is loaded
      if (proView) {
        // ProView
        const proViewInput: ProViewSetInput = removeProperties(
          values,
          'colleagues',
          'googleReviewLinks',
          'sectors',
          'cities',
          'proMessages',
          'proPrices',
          'trades',
          'proPresentation',
          'accounts',
          'proPeople',
          'proPostalAddress',
          'phoneNumbers',
          'isFitInGuide',
        );

        // ProPresentation
        const proPresentationInput: ProPresentationSetInput = removeProperties(
          values.proPresentation,
          'proMediaObjects',
          'mainTrade',
          'secondaryTrade',
          'company',
          'certifications',
          'mainTrades',
        );

        const isCompanyChanged =
          proPresentationInput.companyId !==
          initialValues.proPresentation.companyId;

        // Check if cities have changed to trigger proIntervention update
        const isCitiesChanged = !isDeepEqual(
          values.cities.sort((a, b) => a.id - b.id),
          initialValues.cities.sort((a, b) => a.id - b.id),
        );

        const isMainTradesChanged = !isDeepEqual(
          values.proPresentation.mainTrades.sort((a, b) => a - b),
          initialValues.proPresentation.mainTrades.sort((a, b) => a - b),
        );

        // If company changed or mainTrades changed
        if (isCompanyChanged || isMainTradesChanged) {
          const result = await fetchCompany(
            proPresentationInput.companyId ?? 0,
          );

          const existingMainTradeProView =
            result.data?.companyByPk?.proPresentations.reduce<boolean>(
              (acc, proPresentation) => {
                const ids = proPresentation.mainTrades?.map(
                  (trade) => trade.trade.id,
                );
                if (
                  proPresentation.id !== proView.proPresentation.id && // Except current one
                  values.proPresentation.mainTrades.some((id) =>
                    ids?.includes(id),
                  )
                ) {
                  return true;
                }
                return acc;
              },
              false,
            );

          if (existingMainTradeProView) {
            ToastError(
              'Erreur',
              `Cette entreprise possède déjà une fiche avec la ou les même(s) catégorie(s) principale(s). Veuillez ajouter une nouvelle zone sur la fiche existante. Pour plus d'infos demander au service info.`,
            );
            throw new Error(
              'Cannot link two same mainTrade on the same company',
            );
          }
        }

        if (
          initialValues.proPresentation.companyId &&
          !proPresentationInput.companyId
        ) {
          ToastError(
            'Erreur',
            "Veuillez ajouter une entreprise (une fois une entreprise ajoutée il n'est plus possible de la supprimer)",
          );
          throw new Error('company not selected');
        }

        // Moving contract file to new company
        if (
          isCompanyChanged &&
          initialValues.proPresentation.companyId &&
          proPresentationInput.companyId
        ) {
          await moveContract(
            initialValues.proPresentation.companyId,
            proPresentationInput.companyId,
            proView.id,
          ).catch((error) => {
            ToastError('Erreur', 'Impossible de déplacer le contrat');
            throw error;
          });
        }

        const proPostalAddressInput: ProPostalAddressSetInput =
          removeProperties(values.proPostalAddress, 'formatted', '__typename');

        // GoogleReviewLinks
        // Determine which objects should be inserted and format it for the query (insert objects that do not contains an id)
        const insertGoogleReviewLinks = values.googleReviewLinks
          .filter((googleReviewLink) => !googleReviewLink.id)
          .map((googleReviewLink) => ({
            ...googleReviewLink,
            proPresentationId: proPresentationId,
          }));

        Promise.all(
          insertGoogleReviewLinks.map(async (googleReviewLink) => {
            const googleReviewLinkId = await createGoogleReviewLink({
              ...googleReviewLink,
            });

            googleReviewLink.proViewIds?.forEach(async (proViewId) => {
              googleReviewLinkId &&
                (await updateProViewGoogleReviewLink(
                  proViewId,
                  googleReviewLinkId,
                ));
            });
          }),
        ).catch((error) => {
          ToastError(
            'Erreur',
            "Impossible d'enregistrer la fiche (insert googleReviewLinks)",
          );
          throw error;
        });

        // Determine which objects need to be updated (objects which are already in the database and therefore which contain an id)
        const updateGoogleReviewLinks = values.googleReviewLinks.filter(
          (googleReviewLink) => googleReviewLink.id,
        );

        // Get all ids before any modifications
        const googleReviewLinksInitialIds =
          proView.proPresentation.googleReviewLinks.map(
            (googleReviewLink) => googleReviewLink.id,
          );

        // Get all ids after any modifications
        const googleReviewLinksIds = values.googleReviewLinks.map(
          (googleReviewLink) => googleReviewLink.id,
        );

        const deleteGoogleReviewLinks = googleReviewLinksInitialIds.filter(
          (id) => !googleReviewLinksIds.includes(id),
        );

        // Update entities who need to be updated
        await Promise.all(
          updateGoogleReviewLinks.map(async (googleReviewLink) => {
            if (googleReviewLink.id) {
              await updateGoogleReviewLink(googleReviewLink.id, {
                url: googleReviewLink.url,
              });

              googleReviewLink.proViewIds?.forEach(async (proViewId) => {
                googleReviewLink.id &&
                  (await updateProViewGoogleReviewLink(
                    proViewId,
                    googleReviewLink.id,
                  ));
              });
            }
          }),
        ).catch((error) => {
          ToastError(
            'Erreur',
            "Impossible d'enregistrer la fiche (update googleReviewLinks)",
          );
          throw error;
        });

        // ProMessages
        // Determine which objects should be inserted and format it for the query (insert objects that do not contains an id)
        const insertProMessages = values.proMessages
          .filter((proMessage) => !proMessage.id)
          .map((proMessage): ProMessageInsertInput => ({
            ...proMessage,
            proViewId: proView.id,
          }));

        // Determine which objects need to be updated (objects which are already in the database and therefore which contain an id)
        const updateProMessages = values.proMessages.filter(
          (proMessage) => proMessage.id,
        );

        // Get all ids before any modifications
        const proMessagesInitialIds = proView.proMessages.map(
          (proMessage) => proMessage.id,
        );

        // Get all ids after any modifications
        const proMessagesIds = values.proMessages.map(
          (proMessage) => proMessage.id,
        );

        // Determine which objects should be delete from the database (difference between before/after modifications)
        const deleteProMessages = proMessagesInitialIds.filter(
          (id) => !proMessagesIds.includes(id),
        );

        // Update entities who need to be updated
        await Promise.all(
          updateProMessages.map(async (proMessage) => {
            if (proMessage.id) {
              await updateProMessage(proMessage.id, proMessage);
            }
          }),
        ).catch((error) => {
          ToastError(
            'Erreur',
            "Impossible d'enregistrer la fiche (update proMessages)",
          );
          throw error;
        });

        // ProPrices
        // Determine which objects should be inserted and format it for the query (insert objects that do not contains an id)
        const insertProPrices = values.proPrices
          .filter((proPrice) => !proPrice.id)
          .map((proPrice): ProPriceInsertInput => ({
            ...proPrice,
            proViewId: proView.id,
          }));

        // Determine which objects need to be updated (objects which are already in the database and therefore which contain an id)
        const updateProPrices = values.proPrices.filter(
          (proPrice) => proPrice.id,
        );

        // Get all ids before any modifications
        const proPricesInitialIds = proView.proPrices.map(
          (proPrice) => proPrice.id,
        );

        // Get all ids after any modifications
        const proPricesIds = values.proPrices.map((proPrice) => proPrice.id);

        // Determine which objects should be delete from the database (difference between before/after modifications)
        const deleteProPrices = proPricesInitialIds.filter(
          (id) => !proPricesIds.includes(id),
        );

        // Update entities who need to be updated
        await Promise.all(
          updateProPrices.map(async (proPrice) => {
            if (proPrice.id) {
              await updateProPrice(proPrice.id, proPrice);
            }
          }),
        ).catch((error) => {
          ToastError(
            'Erreur',
            "Impossible d'enregistrer la fiche (update proPrices)",
          );
          throw error;
        });

        // ProMediaObjects
        // Determine which objects should be inserted and format it for the query (insert objects that do not contains an id)
        const insertProMediaObjects = values.proPresentation.proMediaObjects
          .filter((proMediaObject) => !proMediaObject.id)
          .map((proMediaObject): ProMediaObjectInsertInput => ({
            ...proMediaObject,
            proPresentationId: proView.proPresentation.id,
          }));

        // Determine which objects need to be updated (objects which are already in the database and therefore which contain an id)
        const updateProMediaObjects =
          values.proPresentation.proMediaObjects.filter(
            (proMediaObject) => proMediaObject.id,
          );

        // Get all ids before any modifications
        const proMediaObjectsInitialIds =
          proView.proPresentation.proMediaObjects.map(
            (proMediaObject) => proMediaObject.id,
          );

        // Get all ids after any modifications
        const proMediaObjectsIds = values.proPresentation.proMediaObjects.map(
          (proMediaObject) => proMediaObject.id,
        );

        // Determine which objects should be delete from the database (difference between before/after modifications)
        const deleteProMediaObjects = proMediaObjectsInitialIds.filter(
          (proMediaObjectId) => !proMediaObjectsIds.includes(proMediaObjectId),
        );

        // Update entities who need to be updated
        await Promise.all(
          updateProMediaObjects.map(async (proMediaObject) => {
            if (proMediaObject.id) {
              await updateProMediaObject(proMediaObject.id, proMediaObject);
            }
          }),
        ).catch((error) => {
          ToastError(
            'Erreur',
            "Impossible d'enregistrer la fiche (update proMediaObjects)",
          );
          throw error;
        });

        // ProPeople (ProViewProPerson)
        const proPeopleInitialIds = proView.proPeople.map(
          ({ proPerson }) => proPerson.id,
        );

        // Positions are reindexed so that removed contacts leave no gap
        const updateProPeople = values.proPeople.map(
          (proPerson, index): ProViewProPersonSetInput => ({
            position: index + 1,
            isVisible: proPerson.isVisible,
            proPersonId: proPerson.id,
          }),
        );

        const deleteProPeople = proPeopleInitialIds.filter(
          (proPersonId) =>
            !values.proPeople
              .map((proPerson) => proPerson.id)
              .includes(proPersonId),
        );

        // Updating ProViewProPerson (to persist new positions)
        await Promise.all(
          updateProPeople.map(async (proPerson) => {
            if (proPerson.proPersonId) {
              await updateProViewProPerson(
                proPerson.proPersonId,
                proView.id,
                proPerson,
              );
            }
          }),
        ).catch((error) => {
          ToastError(
            'Erreur',
            `Impossible d'enregistrer la fiche (update proViewProPerson)`,
          );
          throw error;
        });

        // Trades
        const tradesInitialIds = proView.trades.map(({ trade }) => trade.id);

        const insertTrades = values.trades
          .filter((tradeId) => !tradesInitialIds.includes(tradeId))
          .map((tradeId): TradeProViewSetInput => ({
            proViewId: proView.id,
            tradeId: tradeId,
          }));

        const deleteTrades = tradesInitialIds.filter(
          (tradeId) => !values.trades.includes(tradeId),
        );

        // Main trades
        const mainTradesInitialIds = proView.proPresentation.mainTrades.map(
          ({ trade }) => trade.id,
        );

        const insertMainTrades = values.proPresentation.mainTrades
          .filter((tradeId) => !mainTradesInitialIds.includes(tradeId))
          .map((tradeId): TradeProPresentationInsertInput => ({
            proPresentationId,
            tradeId: tradeId,
          }));

        const deleteMainTrades = mainTradesInitialIds.filter(
          (tradeId) => !values.proPresentation.mainTrades.includes(tradeId),
        );

        // Cities
        const citiesInitialIds = proView.cities.map((city) => city.city.id);

        const initialParentIds = proView.cities
          .filter(({ city }) => city.parentId)
          .map(({ city }) => city.parentId);

        const insertCities = values.cities.map((city): CityProViewSetInput => ({
          proViewId: proView.id,
          cityId: city.id,
          type: city.type,
        }));

        // Insert parent city if at least one district is selected (if Lyon 7 is selected, insert Lyon)
        values.cities
          .filter((city) => city.parentId)
          .map((city) => {
            if (
              !insertCities.find(
                (insertCity) => insertCity.cityId === city.parentId,
              )
            ) {
              insertCities.push({
                proViewId: proView.id,
                cityId: city.parentId ?? undefined,
                type: city.type,
              });
            }
          });

        const deleteCities = citiesInitialIds.filter(
          (id) => !values.cities.find((city) => city.id === id),
        );

        // Delete parent city if no district is selected
        for (const initialParentId of initialParentIds) {
          if (
            !values.cities.find((city) => city.parentId === initialParentId) &&
            initialParentId
          ) {
            deleteCities.push(initialParentId);
          }
        }

        // Certifications
        const certificationsInitialIds =
          proView.proPresentation.certifications.map(
            ({ certification }) => certification.id,
          );

        const insertCertifications = values.proPresentation.certifications
          .filter((cityId) => !certificationsInitialIds.includes(cityId))
          .map((certificationId): ProCertificationSetInput => ({
            certificationId: certificationId,
            proPresentationId: proPresentationId,
          }));

        const deleteCertifications = certificationsInitialIds.filter(
          (certificationId) =>
            !values.proPresentation.certifications.includes(certificationId),
        );

        // Colleagues
        const colleaguesInitialIds = proView.colleagues.map(
          ({ proView }) => proView.id,
        );

        const insertColleagues = values.colleagues
          .filter((colleagueId) => !colleaguesInitialIds.includes(colleagueId))
          .map((colleagueId): ProViewProViewSetInput => ({
            proViewSource: proView.id,
            proViewTarget: colleagueId,
          }));

        const deleteColleagues = colleaguesInitialIds.filter(
          (colleagueId) => !values.colleagues.includes(colleagueId),
        );

        // Accounts
        const accountInitialIds = proView.accounts.map(
          ({ authUserId }) => authUserId,
        );

        const deleteAccounts = accountInitialIds.filter(
          (accountId) => !values.accounts.includes(accountId),
        );

        try {
          await updatePro({
            proView: {
              ...proViewInput,
              priceComment: values.priceComment?.trim(),
              openingHours: values.openingHours.trim(),
              callTrackingTarget: {
                ...values.callTrackingTarget,
                telephone: PhoneNumber.toE164(
                  values.callTrackingTarget.telephone,
                ),
              },
              callTrackingForward: values.callTrackingForward
                ? {
                    ...values.callTrackingForward,
                    telephone: values.callTrackingForward.telephone
                      ? PhoneNumber.toE164(values.callTrackingForward.telephone)
                      : '',
                  }
                : undefined,
            },
            proPresentationId: proView.proPresentation.id,
            deleteProMediaObjects,
            deleteProMessages,
            insertProPostalAddressOne: {
              ...proPostalAddressInput,
              proViewId: proView.id,
              geoCoordinates: addCrs(proPostalAddressInput.geoCoordinates),
              cityGeoCoordinates: addCrs(
                proPostalAddressInput.cityGeoCoordinates,
              ),
            },
            deleteGoogleReviewLinks,
            insertProMessages,
            insertProPrices,
            deleteProPrices,
            insertProMediaObjects,
            insertTrades,
            deleteTrades,
            insertColleagues,
            deleteColleagues,
            insertCities,
            deleteCities,
            insertCertifications,
            deleteCertifications,
            deleteProPeople,
            deleteAccounts,
            insertMainTrades,
            deleteMainTrades,
          });

          await updatePresentation({
            id: proView.proPresentation.id,
            presentation: {
              ...proPresentationInput,
              name: values.proPresentation.name.trim(),
              profile: values.proPresentation.profile.trim(),
              hobbies: values.proPresentation.hobbies.trim(),
            },
          });

          // V2
          // TODO: Migrate full update logic here.
          // For now, it only handles emitting the company link event.
          await updatePresentationAsync({
            path: { presentationId: proView.proPresentation.id },
            body: {
              companyId: proPresentationInput.companyId,
            },
          });
        } catch (error) {
          ToastError('Erreur', "Impossible d'enregistrer la fiche");
          throw error;
        }

        if (
          proView.proPresentation.slug &&
          proView.proPresentation.slug !== values.proPresentation.slug
        ) {
          await createSlugRedirect(
            proView.proPresentation.slug,
            values.proPresentation.slug,
          ).catch(() => {
            ToastError('Erreur', 'Impossible de créer la redirection de slug');
          });
        }

        if (isCitiesChanged) {
          await updateProIntervention(proView.id).catch(() => {
            ToastError(
              'Erreur',
              'La mise à jour des interventions du pro a échoué',
            );
          });
        }

        /**
         * TODO : Update proView V2
         * we are doing this here to trigger cloud contact synchronization
         */
        updateProViewAsync({
          path: { viewId: proView.id },
          body: {},
        });
        await fetchProForm();

        ToastSuccess('Succès', 'Fiche mise à jour');
      }
    },
    [proView],
  );

  useEffect(() => {
    const fetch = async (): Promise<void> => {
      await fetchProForm();
    };
    fetch();
  }, [fetchProForm]);

  if (!proView) {
    return (
      <Loader
        style={{ marginTop: '50px' }}
        size="large"
        active
        inline="centered"
      >
        Chargement...
      </Loader>
    );
  }

  const initialValues: ProFormUpdateFields = {
    hasContact: proView.hasContact !== undefined ? proView.hasContact : true,
    hasQuotation:
      proView.hasQuotation !== undefined ? proView.hasQuotation : true,
    averageResponseDays: proView.averageResponseDays,
    averageQuotationDays: proView.averageQuotationDays,
    averageProjectDays: proView.averageProjectDays,
    priceComment: proView.priceComment || '',
    status: proView.status,
    avatarHash: proView.avatarHash || '',
    originalAvatarHash: proView.originalAvatarHash || null,
    openingHours: proView.openingHours || '',
    phoneNumbers:
      proView.phoneNumbers?.map((phoneNumber) => {
        return {
          id: phoneNumber.id,
          number: phoneNumber.number,
          tag: phoneNumber.tag,
          status: 'unverified',
        };
      }) || [],
    isSolicitationsDisabled: proView.isSolicitationsDisabled,
    isEnglishSpeaker: proView.isEnglishSpeaker,
    isSmsDisabled: proView.isSmsDisabled,
    isCallsDisabled: proView.isCallsDisabled,
    isCallAlertActivated: proView.isCallAlertActivated,
    isCallRecordable: proView.isCallRecordable,
    isLocalBusiness: proView.isLocalBusiness,
    datePublished: proView.datePublished || null,
    proPeople: proView.proPeople.map((proPerson) => ({
      id: proPerson.proPerson.id,
      position: proPerson.position,
      isVisible: proPerson.isVisible,
    })),
    callTrackingTarget: proView.callTrackingTarget || {
      name: '',
      telephone: '',
    },

    callTrackingForward: proView.callTrackingForward || {
      isActive: false,
      name: '',
      telephone: '',
      timeout: 12,
    },
    emailTarget: proView.emailTarget,
    trades: proView.trades.map(({ trade }) => trade.id),
    accounts: proView.accounts.map(({ authUserId }) => authUserId),
    colleagues: proView.colleagues.map(({ proView }) => proView.id),
    cities: proView.cities.map((city) => ({
      id: city.city.id,
      type: city.type as 'include' | 'exclude',
      parentId: city.city.parentId ?? null,
    })),
    proMessages: proView.proMessages.map((proMessage): ProMessageFields => ({
      id: proMessage.id,
      body: proMessage.body,
      isPermanent: proMessage.isPermanent,
      startDate: proMessage.startDate || null,
      endDate: proMessage.endDate || null,
    })),
    proPrices: proView.proPrices.map((proPrice): ProPriceFields => ({
      id: proPrice.id,
      title: proPrice.title,
      content: proPrice.content,
      isRepair: proPrice.isRepair,
    })),
    proPostalAddress: {
      street: proView.proPostalAddress?.street || '',
      city: proView.proPostalAddress?.city || '',
      postalCode: proView.proPostalAddress?.postalCode || '',
      displayOption: proView.proPostalAddress?.displayOption || 'complete',
      geoCoordinates: proView.proPostalAddress?.geoCoordinates
        ? proView.proPostalAddress?.geoCoordinates
        : undefined,
      cityGeoCoordinates:
        proView.proPostalAddress?.cityGeoCoordinates &&
        isPoint(proView.proPostalAddress?.cityGeoCoordinates)
          ? proView.proPostalAddress?.cityGeoCoordinates
          : undefined,
      formatted: proView.proPostalAddress
        ? formatAddress(proView.proPostalAddress)
        : undefined,
    },

    proPresentation: {
      name: proView.proPresentation.name,
      slug: proView.proPresentation.slug,
      expertise1: proView.proPresentation.expertise1 || '',
      expertise11: proView.proPresentation.expertise11 || '',
      expertise12: proView.proPresentation.expertise12 || '',
      expertise13: proView.proPresentation.expertise13 || '',
      expertise14: proView.proPresentation.expertise14 || '',
      expertise15: proView.proPresentation.expertise15 || '',
      expertise16: proView.proPresentation.expertise16 || '',
      expertise2: proView.proPresentation.expertise2 || '',
      expertise21: proView.proPresentation.expertise21 || '',
      expertise22: proView.proPresentation.expertise22 || '',
      expertise23: proView.proPresentation.expertise23 || '',
      expertise24: proView.proPresentation.expertise24 || '',
      expertise25: proView.proPresentation.expertise25 || '',
      expertise26: proView.proPresentation.expertise26 || '',
      expertise3: proView.proPresentation.expertise3 || '',
      expertise31: proView.proPresentation.expertise31 || '',
      expertise32: proView.proPresentation.expertise32 || '',
      expertise33: proView.proPresentation.expertise33 || '',
      expertise34: proView.proPresentation.expertise34 || '',
      expertise35: proView.proPresentation.expertise35 || '',
      expertise36: proView.proPresentation.expertise36 || '',
      extra1: proView.proPresentation.extra1 || '',
      extra2: proView.proPresentation.extra2 || '',
      extra3: proView.proPresentation.extra3 || '',
      extra4: proView.proPresentation.extra4 || '',
      extra5: proView.proPresentation.extra5 || '',
      mainTrades:
        proView.proPresentation.mainTrades?.map(({ trade }) => trade.id) || [],
      profile: proView.proPresentation.profile || '',
      hobbies: proView.proPresentation.hobbies || '',
      proMediaObjects: proView.proPresentation.proMediaObjects.map(
        (proMediaObject): ProMediaObjectFields => ({
          id: proMediaObject.id,

          hash: proMediaObject.hash,
          position: proMediaObject.position,
        }),
      ),
      certifications: proView.proPresentation.certifications.map(
        ({ certification }) => certification.id,
      ),
      companyId: proView.proPresentation.companyId || null,
    },
    note: proView.note || undefined,
    googleReviewLinks: proView.proPresentation.googleReviewLinks.map(
      (googleReviewLink) => ({
        url: googleReviewLink?.url || '',
        id: googleReviewLink?.id || undefined,
        proViewIds: googleReviewLink.proViews?.map((proView) => proView.id),
        proPresentationId: googleReviewLink.proPresentationId,
      }),
    ),
    isMainCityCenterExcluded: proView.isMainCityCenterExcluded || false,
    isFitInGuide: true,
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnChange={true}
      validationSchema={proFormUpdateValidationSchema(
        proView.proPresentation.slug,
      )}
    >
      <ProFormUpdateView
        proViewId={proView.id}
        proPresentationId={proView.proPresentation.id}
        mainTradeNames={proView.proPresentation.mainTradeNames}
        bilikZone={proView.bilikZone}
        companyId={proView?.proPresentation.companyId}
      />
    </Formik>
  );
};

export default ProFormUpdateContainer;
